import {objectFromEntries} from '@benchling/js-utils';

import {MeasurementUnit} from 'constants/__generated/MeasurementUnit';
import {isDensityUnit, isMassUnit, isMolarityUnit, isVolumeUnit} from 'inventory/InventoryUnitUtil';
import {StoichiometryCalculationMode} from 'notebook/integrations/chemistry/ChemistryStructuredTableConstants';
import {
  preciseDecimalToTypedValue,
  typedValueToPreciseDecimal,
} from 'notebook/integrations/chemistry/custom-evals/ReactionCustomEvalUtils';
import {StoichiometryVariable} from 'notebook/integrations/chemistry/custom-evals/stoichiometry-calculations/StoichiometryEquations';
import {applyStoichiometrySigFigs} from 'notebook/integrations/chemistry/custom-evals/stoichiometry-calculations/StoichiometrySignificantFigures';
import StoichiometrySolver, {
  StoichiometryCalculationVariableValues,
} from 'notebook/integrations/chemistry/custom-evals/stoichiometry-calculations/StoichiometrySolver';
import {
  getStandardChemistryUnitConversionFactor,
  KG_TO_G_CONVERSION_FACTOR,
  resolveUnitsFromTypedValues,
} from 'notebook/integrations/chemistry/custom-evals/stoichiometry-calculations/StoichiometryUnits';
import {StoichiometryCustomEvalOptions} from 'notebook/integrations/chemistry/StoichiometryStructuredTableAdapter';
import {ColumnDefinition} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {StructuredTableColumnGroups} from 'notebook/structured-tables/StructuredTableConstants';
import {CustomEval, CustomEvalResult} from 'notebook/tables/constants/CustomEvalConstants';
import {
  coerceToPrimitiveBoolean,
  isTypedValueEmpty,
  createFloatTypedValue,
  isUnitReferenceTypedValue,
} from 'notebook/tables/typed-values/DataTypes';
import {TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {ResolveQueryFn} from 'notebook/tables/typed-values/TypedValueResolution';
import {StringTypedValue} from 'notebook/tables/typed-values/TypedValueTypes';
import PreciseDecimal from 'util/precise-decimal/PreciseDecimal';
import {assertNever} from 'util/Types';

export type StoichiometryTableCircularColumnGroup =
  | StructuredTableColumnGroups.CHEMISTRY_EQUIVALENCE
  | StructuredTableColumnGroups.CHEMISTRY_MOLES_PLAN
  | StructuredTableColumnGroups.CHEMISTRY_AMOUNT_PLAN;

export type StoichiometryCalculationCellDependencies = [
  error: TypedValue | null,
  isLimitingReactant: TypedValue | null,
  // Limiting Reactant Columns
  limitingReactantEquivalence: TypedValue | null,
  limitingReactantMols: TypedValue | null,
  limitingReactantMolsUnit: TypedValue | null,
  // Component Columns
  equivalence: TypedValue | null,
  moleculePurity: TypedValue | null,
  molecularWeight: TypedValue | null,
  mols: TypedValue | null,
  molUnit: TypedValue | null,
  amount: TypedValue | null,
  amountUnit: TypedValue | null,
  concentration: TypedValue | null,
  concentrationUnit: TypedValue | null,
  formulaWeight: TypedValue | null,
];

export const StoichiometryColumnGroupLimitingReactantCellDependencies: Array<StructuredTableColumnGroups> = [
  StructuredTableColumnGroups.CHEMISTRY_EQUIVALENCE,
  StructuredTableColumnGroups.CHEMISTRY_MOLES_PLAN,
  StructuredTableColumnGroups.CHEMISTRY_MOL_UNIT,
];

export const StoichiometryColumnGroupCellDependencies: Array<StructuredTableColumnGroups> = [
  StructuredTableColumnGroups.CHEMISTRY_EQUIVALENCE,
  StructuredTableColumnGroups.CHEMISTRY_MOLECULE_PURITY,
  StructuredTableColumnGroups.CHEMISTRY_MOLECULAR_WEIGHT,
  StructuredTableColumnGroups.CHEMISTRY_MOLES_PLAN,
  StructuredTableColumnGroups.CHEMISTRY_MOL_UNIT,
  StructuredTableColumnGroups.CHEMISTRY_AMOUNT_PLAN,
  StructuredTableColumnGroups.CHEMISTRY_AMOUNT_UNIT,
  StructuredTableColumnGroups.CHEMISTRY_CONCENTRATION,
  StructuredTableColumnGroups.CHEMISTRY_CONCENTRATION_UNIT,
  StructuredTableColumnGroups.CHEMISTRY_FORMULA_WEIGHT,
];

function cellDependenciesToStoichiometryCalculationVariableValues(
  cellDependencies: StoichiometryCalculationCellDependencies,
  units: Array<MeasurementUnit>
): StoichiometryCalculationVariableValues {
  const [
    _error,
    _isLimitingReactant,
    limitingReactantEquivalence,
    limitingReactantMols,
    limitingReactantMolsUnitTypedValue,
    equivalence,
    moleculePurity,
    molecularWeight,
    mols,
    molUnitTypedValue,
    amount,
    amountUnitTypedValue,
    concentration,
    concentrationUnitTypedValue,
    formulaWeight,
  ] = cellDependencies;

  // Get the full unit objects for each unit in the cell dependencies
  const unitsById = objectFromEntries(units.map((unit) => [unit.id, unit]));
  const limitingReactantMolsUnit = isUnitReferenceTypedValue(limitingReactantMolsUnitTypedValue)
    ? unitsById[limitingReactantMolsUnitTypedValue.unitSummary.id]
    : undefined;
  const molUnit = isUnitReferenceTypedValue(molUnitTypedValue)
    ? unitsById[molUnitTypedValue.unitSummary.id]
    : undefined;
  const amountUnit = isUnitReferenceTypedValue(amountUnitTypedValue)
    ? unitsById[amountUnitTypedValue.unitSummary.id]
    : undefined;
  const concentrationUnit = isUnitReferenceTypedValue(concentrationUnitTypedValue)
    ? unitsById[concentrationUnitTypedValue.unitSummary.id]
    : undefined;

  const limitingReactantMolConversion = getStandardChemistryUnitConversionFactor(limitingReactantMolsUnit);
  const molConversion = getStandardChemistryUnitConversionFactor(molUnit);
  const moleculePurityPercentage = typedValueToPreciseDecimal(moleculePurity);

  const values: StoichiometryCalculationVariableValues = {
    [StoichiometryVariable.LIMITING_REACTANT_EQUIVALENCE]:
      typedValueToPreciseDecimal(limitingReactantEquivalence),
    [StoichiometryVariable.LIMITING_REACTANT_MOLS]:
      typedValueToPreciseDecimal(limitingReactantMols) ?? undefined,
    [StoichiometryVariable.LIMITING_REACTANT_MOL_UNIT_CONVERSION]: limitingReactantMolConversion,
    // convert percentage to decimal notation, only if it's in a valid range
    [StoichiometryVariable.MOLECULE_PURITY]:
      moleculePurityPercentage &&
      moleculePurityPercentage.decimal.gt(0) &&
      moleculePurityPercentage.decimal.lte(100)
        ? PreciseDecimal.of(moleculePurityPercentage.decimal.div(100))
        : undefined,
    [StoichiometryVariable.EQUIVALENCE]: typedValueToPreciseDecimal(equivalence),
    [StoichiometryVariable.MOLS]: typedValueToPreciseDecimal(mols),
    [StoichiometryVariable.MOL_UNIT_CONVERSION]: molConversion,
    // TODO(BNCH-154354): Remove MW fallback once all tables have FW column
    [StoichiometryVariable.FORMULA_WEIGHT]:
      typedValueToPreciseDecimal(formulaWeight) ?? typedValueToPreciseDecimal(molecularWeight),
  };

  if (amountUnit) {
    if (isVolumeUnit(amountUnit)) {
      values[StoichiometryVariable.VOLUME] = typedValueToPreciseDecimal(amount);
      values[StoichiometryVariable.VOLUME_UNIT_CONVERSION] =
        getStandardChemistryUnitConversionFactor(amountUnit);
    } else if (isMassUnit(amountUnit)) {
      values[StoichiometryVariable.MASS] = typedValueToPreciseDecimal(amount);
      values[StoichiometryVariable.MASS_UNIT_CONVERSION] = getStandardChemistryUnitConversionFactor(
        amountUnit,
        KG_TO_G_CONVERSION_FACTOR
      );
    }
  }

  if (concentrationUnit) {
    if (isMolarityUnit(concentrationUnit)) {
      values[StoichiometryVariable.MOLARITY] = typedValueToPreciseDecimal(concentration);
      values[StoichiometryVariable.MOLARITY_UNIT_CONVERSION] =
        getStandardChemistryUnitConversionFactor(concentrationUnit);
    } else if (isDensityUnit(concentrationUnit)) {
      values[StoichiometryVariable.DENSITY] = typedValueToPreciseDecimal(concentration);
      values[StoichiometryVariable.DENSITY_UNIT_CONVERSION] = getStandardChemistryUnitConversionFactor(
        concentrationUnit,
        KG_TO_G_CONVERSION_FACTOR
      );
    }
  }

  return values;
}

export default function makeStoichiometryCalculationCustomEval(
  solveForColumn: StoichiometryTableCircularColumnGroup
): CustomEval<StoichiometryCustomEvalOptions> {
  function stoichiometryCustomEval(
    typedValue: TypedValue,
    columnDefinition: ColumnDefinition,
    cellDependencies: Array<TypedValue | null>,
    customEvalOptions: StoichiometryCustomEvalOptions,
    resolveFromResolutionQuery: ResolveQueryFn
  ): CustomEvalResult {
    const {
      tableMetadata: {calculationMode = StoichiometryCalculationMode.STANDARD},
    } = customEvalOptions;
    // The limiting reactant equivalence is always locked to 1
    const isLimitingReactant = coerceToPrimitiveBoolean(cellDependencies[1]) ?? false;
    if (isLimitingReactant && solveForColumn === StructuredTableColumnGroups.CHEMISTRY_EQUIVALENCE) {
      return {
        val: createFloatTypedValue(1),
        locked: true,
        error: null,
      };
    }
    if (cellDependencies.length <= 1 || !isTypedValueEmpty(typedValue)) {
      const errorValue = cellDependencies[0];
      return {
        val: typedValue,
        error: errorValue ? {type: 'ERROR', message: (errorValue as StringTypedValue).rawValue} : null,
        locked: false,
      };
    }

    // Load any units used in the calculation. If they're still loading, show the current value in the cell.
    const {units, error: unitsResolutionError} = resolveUnitsFromTypedValues(
      cellDependencies,
      resolveFromResolutionQuery
    );
    if (unitsResolutionError != null) {
      return {
        val: typedValue,
        error: unitsResolutionError,
        locked: false,
      };
    }

    const stoichiometryCalculationVariableValues = cellDependenciesToStoichiometryCalculationVariableValues(
      cellDependencies as StoichiometryCalculationCellDependencies,
      units ?? []
    );
    const solveForVariable = getSolveForVariableFromColumn(solveForColumn, {
      unitType: stoichiometryCalculationVariableValues.VOLUME_UNIT_CONVERSION != null ? 'volume' : 'mass',
    });
    const solverResult = StoichiometrySolver.solve(solveForVariable, stoichiometryCalculationVariableValues);
    // If we are missing any values, don't return an empty value. List out missing dependencies
    // in the error message.
    if ('missingDeps' in solverResult) {
      return {
        val: createFloatTypedValue(null),
        error: {type: 'ERROR', message: `Missing dependencies: ${[...solverResult.missingDeps].join(', ')}`},
        locked: false,
      };
    }
    // Otherwise, return the calculated value and lock the cell.
    const {solution, equation} = solverResult;
    let roundedSolution = solution;
    switch (calculationMode) {
      case StoichiometryCalculationMode.SIGFIGS: {
        // Equivalence should not be rounded since it's considered a perfect ratio
        if (solveForVariable !== StoichiometryVariable.EQUIVALENCE) {
          roundedSolution = applyStoichiometrySigFigs(
            solution,
            stoichiometryCalculationVariableValues,
            equation
          );
        }
        break;
      }
      case StoichiometryCalculationMode.STANDARD:
      default:
        break;
    }
    return {
      val: preciseDecimalToTypedValue(roundedSolution),
      error: null,
      locked: true,
    };
  }
  return stoichiometryCustomEval;
}

type StoichiometryAmountUnitType = 'mass' | 'volume';

function getSolveForVariableFromColumn(
  columnGroup: StoichiometryTableCircularColumnGroup,
  options: {unitType: StoichiometryAmountUnitType}
): StoichiometryVariable {
  switch (columnGroup) {
    case StructuredTableColumnGroups.CHEMISTRY_EQUIVALENCE:
      return StoichiometryVariable.EQUIVALENCE;
    case StructuredTableColumnGroups.CHEMISTRY_MOLES_PLAN:
      return StoichiometryVariable.MOLS;
    case StructuredTableColumnGroups.CHEMISTRY_AMOUNT_PLAN:
      switch (options.unitType) {
        case 'mass':
          return StoichiometryVariable.MASS;
        case 'volume':
          return StoichiometryVariable.VOLUME;
        default:
          assertNever(options.unitType);
          break;
      }
      break;
    default:
      assertNever(columnGroup);
      break;
  }
  throw new Error(`Cannot figure out variable to solve for from column group: ${columnGroup}`);
}
